import { useContext, useState } from "react"
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import { NavLink, useParams, useNavigate } from "react-router-dom"
import { AppContext } from "../../../context/AppContext"
import { useMergedViewImage } from "../../../hooks/use-merged-view-image"
import { FormikProvider, useFormik } from "formik"
import { TextField } from "@material-ui/core"
import useReloadWarning from "../../../hooks/use-reload-warning"
import useRequestSubmission from "../../../hooks/use-request-submission"
import Loader from "../../Loader/Loader"
import Modal from "../../Modal/Modal"
import RequestDataForm from "../../RequestDataForm/RequestDataForm"
import Button from "../../buttons/Button"
import SubmissionResult from "../../SubmissionResult/SubmissionResult"

import style from "./Actions.module.scss"
import styles from "../../RequestDataForm/RequestDataForm.module.scss"

const Actions = ({ summary, summaryValues }) => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const appContext = useContext(AppContext)
  const { store, formikRef } = appContext
  const params = useParams()
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [pdfModal, setPdfModal] = useState(false)
  // eslint-disable-next-line no-unused-vars
  const [statusMessage, setStatusMessage] = useState(null)
  // eslint-disable-next-line no-unused-vars
  const [statusError, setStatusError] = useState(false)

  const toggleModal = () => {
    setModalIsOpen(!modalIsOpen)
  }
  const handleCloseModal = () => {
    setModalIsOpen(false)
    setPdfModal(false)
    setStatusMessage(null)
    setStatusError(false)
    formik.resetForm()
  }
  const isViewer = false

  const selectedModel = store?.chargers.find(c => c.code === params.MODELO)
  const model = selectedModel.code
  const currentView = "FRONTAL"
  const imageFolder = store.initConfig.images
  const { generateMergedImage } = useMergedViewImage({
    model: model,
    currentView,
    imageFolder,
  })

  const { submitRequest, loading: loadingPdf } = useRequestSubmission({
    model,
    generateMergedImage,
  })

  const formik = useFormik({
    initialValues: {personal_data: {email:''}},
    validate: formValues => {
      if (!formValues.personal_data.email) {
        return {personal_data : { email: t("formData.requiredfield") }}
      }
    },
    validateOnMount: true,
    onSubmit: async (formValues, { setSubmitting }) => {
      const configurationValues = formikRef?.current?.values || summaryValues
      try {
        const res = await submitRequest({
          configuration: configurationValues,
          isFinalSubmit: false,
          userValues: formValues,
        })

        if (res.success) {
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: "generate_lead",
            contact_type: "ev_charger_form",
            contact_reason: "Receive your configuration",
          })
          const msg = getLabel("Formulario enviado correctamente")
          setStatusMessage(msg)
          setStatusError(false)
        } else {
          const msg = res.error?.message || getLabel("Ha ocurrido un error")
          setStatusMessage(msg)
          setStatusError(true)
        }
      } catch (error) {
        const msg = error?.message || getLabel("Ha ocurrido un error")
        setStatusMessage(msg)
        setStatusError(true)
      } finally {
        setSubmitting(false)
      }
    }
  })

  const getLabel = key => t(key)

  const { handleReloadWarning } = useReloadWarning(true)

  const isMobile = useMediaQuery({ query: "(min-width: 993px)" })

  const Mobile = ({ children }) => {
    return isMobile ? children : null
  }
  
  const renderModalContent = () => {

    const recaptchaEnabled = process.env.REACT_APP_RECAPTCHA_ENABLED === "true";

    return recaptchaEnabled  ? (
      <GoogleReCaptchaProvider
        reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
      >
        <RequestDataForm 
          values={formikRef?.current?.values || summaryValues} 
          onSuccess={msg => {
            setStatusMessage(msg)
            setStatusError(false)
          }}
          onError={msg => {
            setStatusMessage(msg)
            setStatusError(true)
          }}
          closeModal={()=>setModalIsOpen(false)}
        />
      </GoogleReCaptchaProvider>
    ) : (
        <RequestDataForm 
          values={formikRef?.current?.values || summaryValues} 
          onSuccess={msg => {
            setStatusMessage(msg)
            setStatusError(false)
          }}
          onError={msg => {
            setStatusMessage(msg)
            setStatusError(true)
          }}
          closeModal={()=>setModalIsOpen(false)}
        />
    )
  }

  const handleCloseResult = () => {
    setStatusMessage(null)
    setStatusError(false)
    setPdfModal(false)
    formik.resetForm()
  }

  const modalContent = renderModalContent();

  const resetFormik = () => {
    formikRef.current?.resetForm()
    navigate(`/${i18n.language}/configure/${selectedModel.code}`, { state: { from: "summary", reset: true } })
    window.location.reload()
  }

  if (isViewer) {
    return ""
  }

  return (
    <div className={style.actions}>
      <div className={style.left}>
        <div className={style.model}>
          {selectedModel?.title && (
            <p className={style.text}>{selectedModel.title}</p>
          )}
          <NavLink
            title={!isMobile ? t("New design") : ""}
            to={`/${i18n.language}/configure`}
            state={{ canShowWelcome: false }}
            onClick={event => handleReloadWarning(event)}
          >
            <Mobile>
              <span className={style.textlink}> {t("New design")}</span>
            </Mobile>
          </NavLink>
        </div>
        <div className={style.back}></div>
        <button
          className={style.reset}
          type="button"
          onClick={() => resetFormik()}
        >
          <svg
            width="22"
            height="16"
            viewBox="0 0 22 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.95426 0.799949L1.40039 5.41544M1.40039 5.41544L5.95426 10.0309M1.40039 5.41544L16.6004 5.41544C18.8095 5.41544 20.6004 7.2063 20.6004 9.41544L20.6004 11.2C20.6004 13.4091 18.8095 15.2 16.6004 15.2L11.0004 15.2"
              stroke="white"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      <span className={style.separator}>|</span>
      <div className={style.right}>
        {summary && (
          <div className={style.contentBtn}>
            <Button
              type="button"
              onClick={()=>setPdfModal(true)}
              disabled={loadingPdf}
            >
              {loadingPdf ? <Loader /> : t("recibe_tu_configuracion")}
            </Button>
          </div>
        )}
        <div className={style.contentBtn}>
          <Button primary={true} type="button" onClick={toggleModal}>
            {t("Request a quote")}
          </Button>

          {modalContent && (
            <Modal
              large={true}
              isOpen={modalIsOpen}
              onRequestClose={handleCloseModal}
            >
              {modalContent}
            </Modal>
          )}

          <Modal
              large={true}
              isOpen={pdfModal}
              onRequestClose={handleCloseModal}
            >
              {!statusMessage ? (
                <FormikProvider value={formik}>
                  <div className={styles.formContent}>
                    <form className={styles.content} onSubmit={formik.handleSubmit}>
                      <h2 className={styles.h2}>{t("recibe_tu_configuracion_email")}</h2>
                      <hr />
                      <div className={styles.legalText}>
                          {t("texto_legal")}<a onClick={()=>navigate(`/${i18n.language}/privacy-policy/`)}><span className={styles.enlace}>{t("texto_legal_enlace")}</span></a>
                        </div>
                      <div className={styles.contentTop}>
                        <div className={styles.col}>
                          <TextField
                            className={styles.input}
                            id="emailPdf"
                            label={t("formData.email")}
                            variant="filled"
                            name="personal_data.email"
                            value={formik.values?.personal_data?.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched?.personal_data?.email &&
                              Boolean(formik.errors?.personal_data?.email)
                            }
                            helperText={
                              formik.touched?.personal_data?.email &&
                              formik.errors?.personal_data?.email
                            }
                            type="email"
                          />
                        </div>
                        <div className={styles.col}>
                          <Button
                            type="submit"
                            primary={true}
                            disabled={formik.isSubmitting}
                          >
                            {t("formData.Send")}
                          </Button>
                          {formik.isSubmitting && (
                            <div className={styles.submitting}>
                              <Loader />
                            </div>
                          )}
                        </div>
                      </div>
                    </form>
                  </div>
                </FormikProvider>
              ) : (
                <SubmissionResult
                  error={statusError}
                  title={statusError ? t("Error") : t("Thanks")}
                  message={statusMessage}
                  buttonText={t("close")}
                  onButtonClick={handleCloseResult}
                />
              ) }
            </Modal>

        </div>
      </div>
    </div>
  )
}

export default Actions
